/**
 * What a merchant can actually reach, and what this portal cannot yet draw.
 *
 * The mechanism is the four consoles', deliberately re-stated rather than imported — see
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` for why nothing is shared between the channel
 * applications yet. The three questions it answers are the same three:
 *
 *   - visible   — show it in the rail
 *   - reachable — allow it to be opened
 *   - readOnly  — open it, but do not offer the commands
 *
 * What is different here is the tenancy. **This is the first channel on the platform that holds
 * more than a tenant.** A merchant's session carries a tenant *and* a merchant, and the stock board
 * carries a location as well — which is why three of this portal's reads are addressed
 * `/v1/merchants/{merchantId}/…` and why `docs/design/api-operations.json` marks two of them as
 * **not** answering a collection. That flag asks "can a screen render this knowing only the tenant
 * it is acting for", and the honest answer for a merchant's shelf is no. The sweep's own
 * `CHANNELS.merchant.holds` is `["tenantId", "merchantId", "locationId"]`, which is the reading
 * that matters here.
 *
 * ## Three of six, and the three that are not are not route work
 *
 * | page                 | state                                                              |
 * | -------------------- | ------------------------------------------------------------------ |
 * | Incoming orders      | **built**, against COM-06 `GET /v1/merchants/{merchantId}/orders`  |
 * | Stock                | **built**, against COM-03's location listing                       |
 * | Products and menu    | **built**, against COM-02 `GET /v1/tenants/{tenantId}/products`    |
 * | Branches and hours   | **built**, against MER-02's branch listing — hours drawn as absent  |
 * | Payouts and statements | blocked — nothing publishes a payout's period                     |
 * | Staff                | **built**, against IAM-02's member listing filtered by merchant     |
 *
 * `Products and menu` was the one screen of the forty-two that `console-screen-support.test.ts`
 * recorded as **ready here and not drawn**, for one reason: "whose channel has no application at
 * all". This file is that application.
 *
 * The other two were published for it: COM-06's board is paged over the acceptance deadline, which
 * is the design's own order — "Accept or reject each order before the timer runs out" — and COM-03's
 * shelf over the SKU.
 *
 * Nothing here talks to a service. Scopes arrive in the access token, which is the only authority
 * on what a merchant may do, and this module decides only what to draw. Every command still goes to
 * a service that checks again.
 */

/** The scope the incoming orders board spends. */
export const ORDERS_SCOPE = "order-acceptance:read";

/** The scope the stock board spends. */
export const INVENTORY_SCOPE = "inventory:read";

/** The scope the products board spends. */
export const PRODUCTS_SCOPE = "product:read";

/**
 * The scope the branches board would spend, named although the page is blocked.
 *
 * Naming it keeps the citation checkable — `tests/service-codes.test.ts` holds every page's owners
 * to enforcing a scope the page asks for — and it is also how the gap stays visible: MER-02
 * publishes `merchant-location:read` and holds no opening hours, so the page is short of a *field*
 * rather than of a permission.
 */
export const LOCATIONS_SCOPE = "merchant-location:read";

/**
 * The scope the payouts board spends.
 *
 * **It replaced `payout:batch:read`, which is what this constant held while the page was blocked**
 * — and that one would have been the wrong grant even once a listing existed. `payout:batch:read`
 * reads the **tenant's** payout board, where a merchant would see every other merchant on the
 * platform counted, and one scope covering both questions would make the distinction unexpressible
 * in the realm. ADR 0089 §2 publishes a scope of its own for exactly that reason, and ADR 0032 is
 * why it matters here: a channel client is granted only the scopes its own screens spend, so a
 * wrong constant in this file is a wrong grant on the client.
 */
export const PAYOUTS_SCOPE = "payout:beneficiary:read";

/**
 * The scope the staff board spends.
 *
 * **It replaced `workforce:subject:resolve`, which is what this constant held while the page was
 * blocked.** That one is what a service spends during sign-in to turn a federated subject into a
 * platform subject; listing the people recorded against a shop is what a manager does, and ADR 0084
 * §6 separates them. A sign-in path holding the read could enumerate a tenant's whole staff.
 */
export const STAFF_SCOPE = "workforce:member:read";

export interface PageDefinition {
  readonly id: string;
  /** The page's own title, as the design's `PAGES` table writes it. */
  readonly title: string;
  /** The rail's label, where the design's menu uses a shorter one than the page title. */
  readonly railLabel?: string;
  /** The design's `NAV` group this item sits in, in that file's own words. */
  readonly section: string;
  /** Read requirements, OpenAPI's shape: any of these alternatives, all scopes within one. */
  readonly readRequirements: readonly (readonly string[])[];
  readonly writeRequirements?: readonly (readonly string[])[];
  /** Why the page exists, in the merchant's terms — the design's own `PAGES` description. */
  readonly purpose: string;
  /** Why this page cannot be built yet, if it cannot. */
  readonly blockedBy?: string;
  /** The services this page reads from, by their canonical identifiers. */
  readonly owners: readonly string[];
  /** The Material Symbols Rounded glyph, read from `NAV` in the design file. */
  readonly glyph: string;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * The portal's pages, in the design file's own order and groups.
 *
 * The design's `NAV` is three groups: `Today` — orders, stock — `Shop` — menu, hours — and
 * `Business` — payouts, staff. Kept in that order, so the rail reads the way the design's does.
 */
export const merchantPages: readonly PageDefinition[] = [
  {
    id: "orders",
    glyph: "receipt_long",
    owners: ["COM-06"],
    title: "Incoming orders",
    section: "Today",
    purpose:
      "Accept or reject each order before the timer runs out, then move it through preparation.",
    /*
     * Built, on the listing published for it. COM-06 answers `orderId`, `itemUnits`,
     * `acceptanceDeadline`, `state` and `version` per row, which is the design's `Order`, its
     * `Items` count and the timer its `Timer` column counts down to.
     *
     * **The money in the design's first column has no source on this board.** It writes
     * `co_7Q31 · 3 items · KES 1,240.00`, and COM-06 holds the unit count and not the order total:
     * the total is COM-05's, which this page has no route to and would be a second service on one
     * row. The cell draws the identifier and the units, which is two of the three things the
     * design puts there.
     *
     * **COM-07 is deliberately not an owner.** `SCREEN_SPEC.md` cites it beside COM-06 for the
     * preparation half — the design's `preparing` and `ready` states — and its reads are
     * `/v1/order-preparations/{orderId}`, by identifier, so a board cannot ask it anything. The
     * states this board draws are COM-06's own four.
     */
    readRequirements: [[ORDERS_SCOPE]],
    /*
     * The accept and reject commands are published and deliberately not asked for. The design's
     * primary action is `Pause new orders`, which is a different thing entirely and has no
     * operation anywhere — see `IncomingOrders.tsx` for the refusal. A board holding the accept
     * scope with no accept control would be a permission with no screen behind it.
     */
  },
  {
    id: "stock",
    glyph: "inventory_2",
    owners: ["COM-03"],
    title: "Stock",
    section: "Today",
    purpose:
      'Report stock so customers see a live count. Items without one show as "not reported".',
    /*
     * Built, on the listing published for it. COM-03 answers `sku`, `availableQuantity`,
     * `updatedAt` and `updatedBy` per row, which is the design's `Product`, `Count` and `Updated`.
     *
     * **The design's own closing note is the gap, and it says so itself**: "G-36: the service
     * stores only a count and time, so freshness is worked out from age". This board does exactly
     * that, which is the one case on this platform where a design file names its own absence and
     * prescribes the treatment.
     *
     * **`not reported` is not drawable and is a deeper absence than a count.** A SKU with no row
     * has never been reported to COM-03, so "not reported" is a fact about the *product catalogue*
     * rather than about inventory — this listing cannot see it, and joining COM-02's products to
     * find it would be a second service on one board. `Stock.tsx` records it.
     *
     * The design's `Product` column is a name — `Avocado, Hass` — and COM-03 holds a SKU. The cell
     * draws the SKU in mono and says which.
     */
    readRequirements: [[INVENTORY_SCOPE]],
  },
  {
    id: "menu",
    glyph: "restaurant_menu",
    owners: ["COM-02"],
    title: "Products and menu",
    section: "Shop",
    purpose: "What customers can buy, with prices, photos and options.",
    /*
     * Built, and the one screen of the forty-two that `console-screen-support.test.ts` recorded as
     * **ready here and not drawn** — for one reason: "whose channel has no application at all".
     *
     * COM-02 answers `name`, `categoryId`, `price`, `sku` and `status` per row. The design's three
     * columns are `Product`, `Category` and `Price`, and two of the three are published outright.
     *
     * **The category is an identifier where the design draws a name.** `Fruit and veg` lives on
     * COM-01's catalogue as a category entry, and resolving it per row means reading the catalogue
     * this product belongs to — a second service on one board. The cell draws the identifier in
     * mono and says so. (COM-01's catalogue listing was published on the same day and does not
     * help: it answers catalogues, and the name is inside one.)
     *
     * **Two of the design's four tiles are absences rather than counts**: `No photo · 12` needs a
     * photo field, which COM-02 does not publish at all, and `Price changes pending · 2` needs a
     * future effective date, which it does not either. `Products and menu` draws no tiles, like
     * every other board on this platform (R-04).
     */
    readRequirements: [[PRODUCTS_SCOPE]],
  },
  {
    id: "hours",
    glyph: "schedule",
    railLabel: "Branches",
    owners: ["MER-02"],
    title: "Branches and hours",
    section: "Shop",
    purpose: "Where you trade from, when you are open, and how busy you are.",
    /*
     * **Served, and the reason that stood here was wrong about which half was missing.** It read:
     * nothing publishes a branch's opening hours *or its usual preparation time*, and this is "a
     * field absence and not a route absence".
     *
     * `preparation_minutes` has been on `merchant_locations` since that table's first migration,
     * with a `CHECK (BETWEEN 0 AND 1440)`. And it **was** a route absence as well as a field one:
     * every MER-02 operation was addressed by `locationId`, so a screen could read, update or
     * close a branch it already had an identifier for and could not discover one.
     * [ADR 0087](../../../docs/adr/0087-a-branch-nobody-could-list.md) publishes the listing.
     *
     * **The opening hours half was right and is still true.** Nothing on this platform holds an
     * opening hour — swept across every contract — so that column is drawn as an absence and the
     * badge shows the branch's status instead, which is a different statement. Two of the
     * design's three badge words and its `Mashujaa Day` row are absent too: a busy mode is
     * modelled by nothing, and a holiday is not a branch.
     *
     * **The design's chip is corrected.** It reads `COM-01 · GEO`; COM-01 holds catalogues and
     * there is no service called GEO. `service-codes.test.ts` holds every code named here to
     * being a service this page reads from, which is why the correction is in the code rather
     * than in prose.
     */
    readRequirements: [[LOCATIONS_SCOPE]],
  },
  {
    id: "payouts",
    glyph: "payments",
    owners: ["FIN-07"],
    title: "Payouts and statements",
    section: "Business",
    purpose: "What you have earned, what the platform kept, and when you get paid.",
    /*
     * **Served, and its recorded reason was right about both halves — the only one in this cohort
     * that was.** The entry that stood here read:
     *
     * > Nothing publishes the period a payout is for — not payout.periodStart, not
     * > payout.periodEnd, not batch.periodStart — and the design's middle column is that period.
     * > FIN-07's batch listing is also the tenant's rather than this merchant's, so a merchant
     * > reading it would see every other merchant's payouts. Both have to be answered, and the
     * > first is a field nothing holds.
     *
     * Three screens before this one had notes that were wrong about which half was missing, so this
     * was re-measured expecting the same and it held. ADR 0089 answers the second half: a
     * beneficiary-scoped listing, under a scope of its own, withholding the bank destination and
     * the tenant's own staff subjects.
     *
     * **The period is still absent and the board draws it as one**, which is why this page is
     * degraded rather than whole. `requestedAt` is the near miss and is refused on purpose: a week
     * derived by subtracting seven days from it would be this portal inventing a settlement
     * calendar, wrong for any tenant that pays fortnightly or on demand.
     *
     * **The design's chip is corrected.** It reads `FIN-06 · FIN-07`; FIN-06 is the commission
     * service, behind the `Commission this week` tile that is not drawn, so this page reads nothing
     * from it. Second chip corrected on this portal, after `hours`.
     */
    readRequirements: [[PAYOUTS_SCOPE]],
  },
  {
    id: "staff",
    glyph: "group",
    owners: ["IAM-02"],
    title: "Staff",
    section: "Business",
    purpose: "Who at your shop can accept orders or change the menu.",
    /*
     * **Served, and it is the one of this portal's three blocked pages whose own reason said what
     * kind of work it would take.** The entry that stood here read:
     *
     * > Nothing on the platform models a staff member: no name, no role, no branch. IAM-02 holds
     * > identity providers and subject links, which is how a person signs in rather than who they
     * > are — and the design's three columns are all three of those fields. **This is a resource
     * > that does not exist, not a route that is not published.**
     *
     * That last sentence is why publishing a fourth listing for this portal would not have helped,
     * and it is the distinction `hours` and `payouts` below still turn on — each needs a **field**
     * that nothing publishes, where this needed a whole record. ADR 0084 adds it.
     *
     * All three of the design's columns are drawn. The `Branch` cell shows an identifier rather
     * than `Kilimani`, because a branch's name is MER-02's and reading it would be one request per
     * branch on a column — the same absence this portal already carries for a product's category.
     */
    readRequirements: [[STAFF_SCOPE]],
  },
] as const;

/**
 * Whether the granted scopes satisfy any one of the alternatives outright.
 *
 * `every` inside `some`: a partly-held requirement is not partial access to a route, it is a
 * refusal.
 */
function satisfies(
  granted: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some((alternative) => alternative.every((scope) => granted.has(scope)));
}

export function resolvePage(page: PageDefinition, granted: ReadonlySet<string>): ResolvedPage {
  // A page the platform cannot support is forbidden to everyone, including a merchant holding every
  // scope in existence. On this portal that is three of six, which is why the rail's foot has
  // something to say.
  if (page.blockedBy !== undefined) return { page, access: "forbidden", visible: false };

  const canRead = satisfies(granted, page.readRequirements);
  const canWrite =
    page.writeRequirements !== undefined && satisfies(granted, page.writeRequirements);

  if (!canRead && canWrite) return { page, access: "full", visible: false };
  if (!canRead) return { page, access: "forbidden", visible: false };
  return { page, access: canWrite ? "full" : "read_only", visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/** Navigation for one merchant, grouped as the design groups it, with empty groups dropped. */
export function buildNavigation(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = merchantPages,
): NavigationSection[] {
  const sections: NavigationSection[] = [];
  for (const page of pages) {
    const resolved = resolvePage(page, granted);
    if (!resolved.visible) continue;
    const existing = sections.find((candidate) => candidate.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else (existing.pages as ResolvedPage[]).push(resolved);
  }
  return sections;
}

/** Whether a merchant can open a page they navigated to directly. */
export function canOpen(pageId: string, granted: ReadonlySet<string>): boolean {
  const page = merchantPages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, granted).access !== "forbidden";
}

/** Every scope this portal can make use of, for a token request or an audit. */
export function declaredScopes(pages: readonly PageDefinition[] = merchantPages): string[] {
  const scopes = new Set<string>();
  for (const page of pages) {
    if (page.blockedBy !== undefined) continue;
    for (const scope of page.readRequirements.flat()) scopes.add(scope);
    for (const scope of (page.writeRequirements ?? []).flat()) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * How many pages this merchant cannot see, and what each would need.
 *
 * Only pages hidden by scope. A page the platform cannot support is invisible to everybody, so
 * counting it here would tell them their role is short of something no role has — and on this
 * portal it would say three.
 */
export function pagesNotInRole(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = merchantPages,
): { id: string; title: string; needs: readonly string[] }[] {
  return pages
    .filter((page) => page.blockedBy === undefined && !resolvePage(page, granted).visible)
    .map((page) => ({
      id: page.id,
      title: page.title,
      needs:
        [...page.readRequirements]
          .map((alternative) => alternative.filter((scope) => !granted.has(scope)))
          .sort((left, right) => left.length - right.length)[0] ?? [],
    }));
}

/** The pages the platform cannot support yet, and why. */
export function blockedPages(
  pages: readonly PageDefinition[] = merchantPages,
): { id: string; title: string; blockedBy: string }[] {
  return pages
    .filter((page): page is PageDefinition & { blockedBy: string } => page.blockedBy !== undefined)
    .map((page) => ({ id: page.id, title: page.title, blockedBy: page.blockedBy }));
}
